import { useState, useEffect, useRef, useMemo } from "react";
import debounce from "lodash/debounce";

import {
  apiCheckPublicCompliance,
  apiUpdatePublicPolicyProjectContent,
} from "../services/PolicyStudioService";
import {
  PolicyAdjustment,
  PolicyProject,
  PolicyProjectType,
} from "winslow-core";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../config";
import showdown from "showdown";
import html2pdf from "html2pdf.js";
import {
  savePublicPolicyStudioState,
  getPublicPolicyStudioState,
} from "../utils/localStorage";
import config from "environment";
import { postPublicPolicyStudioFileUpload } from "../services/client";
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket";

interface PublicPolicyStudioState {
  email: string | null;
  policiesCreated: number;
  updatesMade: number;
}

const converter = new showdown.Converter();
const initialSavedState = getPublicPolicyStudioState();

export const usePublicPolicyStudio = () => {
  const createType = new URLSearchParams(window.location.search).get("create");
  const previewRef = useRef<HTMLDivElement>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyProject | null>(
    null
  );
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [drawerIsLoading, setDrawerIsLoading] = useState(false);
  const [isComplianceDrawerOpen, setIsComplianceDrawerOpen] = useState(false);
  const [isConflictDrawerOpen, setIsConflictDrawerOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [email, setEmail] = useState<string | null>(
    initialSavedState?.email || null
  );
  const [policiesCreated, setPoliciesCreated] = useState(
    initialSavedState?.policiesCreated || 0
  );
  const [updatesMade, setUpdatesMade] = useState(
    initialSavedState?.updatesMade || 0
  );
  const [isPolicyGateModalOpen, setIsPolicyGateModalOpen] = useState(false);
  const [isEmailGated, setIsEmailGated] = useState(false);
  const [isPolicyGated, setIsPolicyGated] = useState(false);
  const [isConfirmNewPolicyModalOpen, setIsConfirmNewPolicyModalOpen] =
    useState(false);
  const [isPolicyStudioShutdownModalOpen, setIsPolicyStudioShutdownModalOpen] =
    useState(false);
  const [
    isConfirmNewPolicyFromFileModalOpen,
    setIsConfirmNewPolicyFromFileModalOpen,
  ] = useState(false);
  const [isCreateFromFileModalOpen, setIsCreateFromFileModalOpen] =
    useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const [warningModalMessage, setWarningModalMessage] = useState("");

  const { sendJsonMessage, lastJsonMessage } = useWebSocket(
    config.wsPolicyStudioUrl,
    {
      onOpen: () => console.log("[websocket] opened"),
      onClose: (code: any) => console.log("[websocket] closed: ", code),
      shouldReconnect: (closeEvent) => true,
      onError: (event: WebSocketEventMap["error"]) =>
        console.log("[websocket] error: ", event),
    }
  );

  const setStreamingFalse = useMemo(
    () => debounce(() => setIsStreaming(false), 2000),
    [setIsStreaming]
  );

  useEffect(() => {
    if (config.killPolicyStudio) {
      setIsPolicyStudioShutdownModalOpen(true);
      return;
    }

    const savedStateStr = getPublicPolicyStudioState();

    if (savedStateStr) {
      try {
        const savedState = JSON.parse(savedStateStr) as PublicPolicyStudioState;
        setEmail(savedState.email);
        setPoliciesCreated(savedState.policiesCreated);
        setUpdatesMade(savedState.updatesMade);

        if (savedState.policiesCreated >= 3 || savedState.updatesMade >= 75) {
          // setTimeout(() => {
          //   setPolicyGate();
          // }, 1000);
          // return;
        }
      } catch (error) {
        console.error("Failed to parse saved state:", error);
      }
    }

    if (createType) {
      policyActions.createPolicy(createType as PolicyProjectType);
    }
  }, []);

  useEffect(() => {
    return () => {
      setStreamingFalse.cancel();
    };
  }, [setStreamingFalse]);

  useEffect(() => {
    console.log("[PolicyStudio] socket message incoming: ", lastJsonMessage);

    if (lastJsonMessage !== null) {
      const response = lastJsonMessage as {
        policyProject?: PolicyProject;
        content?: string;
        conflicts?: {
          conflictsContent: string;
          linkRecordIds: string[];
        };
        compliance?: {
          complianceContent: string;
          jurisdictionContent: string;
        };
      };

      if (
        response.content ||
        response.conflicts?.conflictsContent ||
        response.compliance?.complianceContent
      ) {
        setIsStreaming(true);
        setStreamingFalse();
      }

      if (response.policyProject) {
        setSelectedPolicy(response.policyProject);

        setIsLoading(false);
      } else if (response.content && selectedPolicy) {
        // Handle content-only updates during streaming
        setSelectedPolicy((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            content: prev.content + response.content!,
          };
        });
      } else {
        toast("Unable to update policy project", toastFail);
      }
      setIsLoading(false);
    }
  }, [lastJsonMessage]);

  useEffect(() => {
    //dont' update state for the initial page load
    if (email === null && policiesCreated === 0 && updatesMade === 0) {
      return;
    }

    const state: PublicPolicyStudioState = {
      email,
      policiesCreated,
      updatesMade,
    };
    savePublicPolicyStudioState(JSON.stringify(state));

    if ((policiesCreated > 1 || updatesMade > 20) && !email) {
      //setEmailGate();
      return;
    }

    if (policiesCreated > 3 || updatesMade > 75) {
      //setPolicyGate();
      return;
    }

    //clearGates();
  }, [selectedPolicy, email, policiesCreated, updatesMade]);

  const setEmailGate = () => {
    setIsEmailGated(true);
    setIsPolicyGated(true);
    setIsEmailModalOpen(true);
    setIsPolicyGateModalOpen(false);
  };

  const setPolicyGate = () => {
    setIsEmailGated(false);
    setIsPolicyGated(true);
    setIsEmailModalOpen(false);
    setIsPolicyGateModalOpen(true);
  };

  const clearGates = () => {
    setIsEmailGated(false);
    setIsPolicyGated(false);
    setIsEmailModalOpen(false);
    setIsPolicyGateModalOpen(false);
  };

  const applyGates = (): boolean => {
    let gateApplied = false;
    if ((policiesCreated >= 1 || updatesMade >= 20) && !email) {
      //setEmailGate();
      gateApplied = true;
    }

    if (policiesCreated >= 3 || updatesMade >= 75) {
      //setPolicyGate();
      gateApplied = true;
    }

    return gateApplied;
  };

  const updatePolicyState = (updatedPolicy: PolicyProject) => {
    setSelectedPolicy(updatedPolicy);
  };

  const policyActions = {
    createPolicy: async (type: PolicyProjectType) => {
      setIsLoading(true);
      try {
        sendJsonMessage({
          action: "public-create",
          type,
          email: email || "no_email",
        });

        setPoliciesCreated((prev: number) => prev + 1);
      } catch (error) {
        console.error("Failed to create policy:", error);
      } finally {
        setIsLoading(false);
      }
    },

    updateFromAdjustment: async (adjustment: PolicyAdjustment) => {
      if (!selectedPolicy) return;
      setIsLoading(true);
      setSelectedPolicy({
        ...selectedPolicy,
        content: "",
      });
      sendJsonMessage({
        action: "public-update-from-adjustment",
        currentProject: selectedPolicy,
        email: email || "no_email",
        adjustment,
      });

      setIsLoading(false);
    },

    updateFromPrompt: async (prompt: string) => {
      // if (updatesMade === 24) {
      //   setWarningModalMessage("50");
      // }
      // if (updatesMade === 49) {
      //   setWarningModalMessage("25");
      // }
      // if (updatesMade === 73) {
      //   setWarningModalMessage("1");
      // }

      if (!selectedPolicy) return;
      setIsLoading(true);
      setSelectedPolicy({
        ...selectedPolicy,
        content: "",
      });

      sendJsonMessage({
        action: "public-update-from-prompt",
        currentProject: selectedPolicy,
        email: email || "no_email",
        prompt,
      });
      setUpdatesMade((prev: number) => prev + 1);
      setIsLoading(false);
    },

    updateTitle: async (newTitle: string) => {
      if (!selectedPolicy) return;
      setIsLoading(true);
      const updatedPolicy = {
        ...selectedPolicy,
        title: newTitle,
      };
      if (updatedPolicy) {
        console.log("updateTitle->updatedPolicy", updatedPolicy);
        updatePolicyState(updatedPolicy);
      }
      setIsLoading(false);
    },

    updateContent: async (markdownContent: string) => {
      if (!selectedPolicy) return;
      setIsLoading(true);

      try {
        const updatedPolicy = await apiUpdatePublicPolicyProjectContent(
          selectedPolicy,
          markdownContent,
          email
        );

        const contentUpdatedPolicy = {
          ...selectedPolicy,
          content: updatedPolicy.content,
        };

        if (contentUpdatedPolicy) {
          updatePolicyState(contentUpdatedPolicy);
        }
      } catch (error) {
        console.error("Failed to update policy content:", error);
      } finally {
        setIsLoading(false);
      }
    },

    checkCompliance: async (jurisdictionContent: string) => {
      if (!selectedPolicy?.id) return;
      setDrawerIsLoading(true);
      const updatedPolicyProject = await apiCheckPublicCompliance(
        selectedPolicy,
        jurisdictionContent,
        email
      );
      if (updatedPolicyProject) {
        updatePolicyState(updatedPolicyProject);
      }

      setDrawerIsLoading(false);
    },

    updateEmail: async (newEmail: string) => {
      setEmail(newEmail);
      setIsEmailModalOpen(false);
      setIsConfirmNewPolicyModalOpen(true);
    },

    exportToPdf: async (title: string, content: string) => {
      if (!selectedPolicy?.id) {
        toast("No policy selected for export", toastFail);
        return;
      }

      try {
        const html = converter.makeHtml(content);
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        tempDiv.style.cssText = `
          font-family: Arial, sans-serif;
          padding: 20px;
          max-width: 8.2in;
        `;

        const fileName = `${title}-${new Date().toISOString().split("T")[0]}.pdf`;
        const options = {
          margin: 0.5,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "letter" },
        };

        document.body.appendChild(tempDiv);
        await html2pdf().set(options).from(tempDiv).save();
        document.body.removeChild(tempDiv);

        toast("PDF downloaded successfully", toastSuccess);
      } catch (error) {
        console.error("PDF export failed:", error);
        toast("Failed to generate PDF", toastFail);
      }
    },

    handleCreateFromFileClick: () => {
      //check gates
      // const gateApplied = applyGates();
      // if (gateApplied) {
      //   return;
      // }

      if (selectedPolicy) {
        setIsConfirmNewPolicyFromFileModalOpen(true);
        return;
      }

      setIsCreateFromFileModalOpen(true);
    },

    handleCloseCreateFromFileModal: () => {
      setIsCreateFromFileModalOpen(false);
    },

    uploadFile: async (file: File, type: PolicyProjectType) => {
      setIsLoading(true);
      const response = await postPublicPolicyStudioFileUpload(
        file,
        type,
        email
      );
      if (response.data.error) {
        toast("Unable to upload file, please try again.", toastFail);
        console.error("Failed to upload file:", response.data.error);
      } else {
        const policyProject = response.data.data as PolicyProject;
        setSelectedPolicy(policyProject);
        sendJsonMessage({
          action: "public-create-from-file",
          currentProject: policyProject,
          email: email || "no_email",
        });
      }
    },

    handleCloseWarningModal: () => {
      setWarningModalMessage("");
    },

    handleConfirmNewPolicyFromFile: () => {
      setIsCreateFromFileModalOpen(true);
      setIsConfirmNewPolicyFromFileModalOpen(false);
    },

    handleCloseConfirmNewPolicyFromFileModal: () => {
      setIsConfirmNewPolicyFromFileModalOpen(false);
    },

    handleConfirmNewPolicy: () => {
      setIsCreateModalOpen(true);
      setIsConfirmNewPolicyModalOpen(false);
    },

    handleCloseConfirmNewPolicyModal: () => {
      setIsConfirmNewPolicyModalOpen(false);
    },

    handleAddNewClick: () => {
      // const gateApplied = applyGates();
      // if (gateApplied) {
      //   return;
      // }

      if (selectedPolicy) {
        setIsConfirmNewPolicyModalOpen(true);
        return;
      }

      setIsCreateModalOpen(true);
    },

    handleCloseCreateModal: () => {
      setIsCreateModalOpen(false);
    },
  };

  const drawerActions = {
    openConflictsDrawer: () => setIsConflictDrawerOpen(true),
    closeConflictsDrawer: () => setIsConflictDrawerOpen(false),
    openComplianceDrawer: () => setIsComplianceDrawerOpen(true),
    closeComplianceDrawer: () => {
      setIsComplianceDrawerOpen(false);
    },
  };

  return {
    refs: {
      previewRef,
    },
    state: {
      selectedPolicy,
      isLoading,
      drawerIsLoading,
      isComplianceDrawerOpen,
      isConflictDrawerOpen,
      linkRecords: [],
      email,
      policiesCreated,
      updatesMade,
      isEmailGated,
      isPolicyGated,
      isEmailModalOpen,
      isPolicyGateModalOpen,
      warningModalMessage,
      isCreateModalOpen,
      isConfirmNewPolicyModalOpen,
      isPolicyStudioShutdownModalOpen,
      isCreateFromFileModalOpen,
      isConfirmNewPolicyFromFileModalOpen,
      isStreaming,
    },
    actions: {
      setSelectedPolicy,
      ...policyActions,
      ...drawerActions,
    },
  };
};
